import { Heading } from '@seek/ai-elements';
import { Box, Stack } from 'braid-design-system';

import SkeletonLine from 'src/components/SkeletonLine/SkeletonLine';
import { useIsHomepageGenAIChatAltPositionEnabled } from 'src/store/featureFlags/hooks';

import { useSignedInDashboardContext } from '../SignedInDashboardContextProvider';

import { CareerFeedAIAssistantContainer } from './CareerFeedAIAssistantContainer';
import { CareerFeedAIAssistantContent } from './CareerFeedAIAssistantContent/CareerFeedAIAssistantContent';
import { CareerFeedMobileWebFeatureFlagContainer } from './CareerFeedMobileWebFeatureFlagContainer';
import { useCareerFeedAIAssistant } from './hooks/useCareerFeedAIAssistant';

const RECOMMENDED_JOBS_SPACE = 'small';

/** *
 * This component is responsible for displaying the AI Assistant for the Career Feed.
 * This is still in in discovery phase and the implementation of this component might change drastically.
 * The reason why this is in the code base is so that people can start playing around with it
 * and get feedback on the implementation.
 */
export const CareerFeedAIAssistant = () => {
  const {
    recommendationsResult: {
      error: isRecommendationError,
      loading: isRecommendationLoading,
      isRefreshLoading,
      rawRecommendedJobs,
    },
  } = useSignedInDashboardContext();

  const { persistedCandidateChatContext, progress } =
    useCareerFeedAIAssistant();
  const isHomepageGenAIChatAltPositionEnabled =
    useIsHomepageGenAIChatAltPositionEnabled();

  const jobsCount = rawRecommendedJobs?.globalJobs.length ?? 0;
  const isLoadingState =
    (isRecommendationLoading && !isRefreshLoading) ||
    (progress === 'initial' && isRefreshLoading);

  if (isRecommendationError) {
    return null;
  }

  if (!persistedCandidateChatContext && !isLoadingState && jobsCount === 0) {
    return null;
  }

  return (
    <CareerFeedMobileWebFeatureFlagContainer
      mobileView={<CareerFeedAIAssistantContent />}
      tabletAndAboveView={
        <Stack space={RECOMMENDED_JOBS_SPACE}>
          {!isHomepageGenAIChatAltPositionEnabled && (
            <Box
              userSelect="none"
              display={{ mobile: 'none', desktop: 'block' }}
            >
              <Heading level="3">&nbsp;</Heading>
            </Box>
          )}
          <CareerFeedAIAssistantContainer showHeading={!isLoadingState}>
            {isLoadingState ? (
              <Stack space="medium">
                <SkeletonLine type="text" width={200} size="small" />
                <SkeletonLine type="text" width={260} size="xsmall" />
              </Stack>
            ) : (
              <CareerFeedAIAssistantContent />
            )}
          </CareerFeedAIAssistantContainer>
        </Stack>
      }
    />
  );
};
